/**
 * ClusterBoard — dark neon, iki görünüm: Nöral Harita + Ağaç (sütun)
 * Backend bağlantısı değiştirilmedi; sadece UI/UX yenilendi.
 */
import { useState, useMemo, useEffect } from "react";
import {
  Crown, FileText, ChevronDown, ChevronRight,
  Plus, Pencil, Trash2, Check, X, Star, Globe2,
  LayoutGrid, Layers, StickyNote, FolderTree, Languages, GripVertical,
  Share2, List,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useGetClusterPages, getClusterPages } from "@workspace/api-client-react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ClusterNeuralMap } from "./ClusterNeuralMap";
import { ClusterDetailPanel } from "./ClusterDetailPanel";

// ─── Neon design tokens ───────────────────────────────────────────────────────

const N = {
  bg: "#fafaf9",
  surface: "#ffffff",
  elevated: "#f4f4f2",
  border: "#e7e7e4",
  borderSubtle: "#e8edf3",
  textPrimary: "#1d1d1b",
  textSecondary: "#70706c",
  textMuted: "#70706c",
  blue: "#ec6461",
  purple: "#e83e3c",
  green: "#12b76a",
  cyan: "#e83e3c",
  amber: "#f79009",
  red: "#f04438",
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BoardCluster {
  id: number;
  name: string;
  parentId?: number | null;
  pageCount?: number;
  avgScore?: number | null;
  pillarPageTitle?: string | null;
  pillarPageUrl?: string | null;
  children?: BoardCluster[];
}

export interface BoardPage {
  id: number;
  url: string;
  title?: string | null;
  score?: number | null;
  isPillar?: boolean;
  isFeatured?: boolean;
}

export interface ClusterBoardProps {
  tree: BoardCluster[];
  domainId: number;
  orphanCount: number;
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => void;
  onAddCluster: (name: string, parentId?: number | null) => Promise<void>;
  onSetPillar?: (clusterId: number, pageId: number | null) => Promise<void>;
  onToggleFeatured?: (pageId: number, isFeatured: boolean) => void;
  selectedIds: Set<number>;
  onSelectToggle: (id: number, checked: boolean) => void;
  onMerge: () => void;
  /** Header'dan kontrol edilen görünüm */
  view?: "neural" | "tree";
  /** Neural map merkezinde gösterilecek domain URL */
  domainUrl?: string;
  /** Neural map: sayfayı sürükleyip başka cluster'a taşı (null = atanmamışa al) */
  onMovePage?: (pageId: number, targetClusterId: number | null) => Promise<void>;
  /** Neural map: cluster'ı sürükleyip başka cluster'ın altına taşı */
  onMoveCluster?: (clusterId: number, newParentId: number | null) => Promise<void>;
  /** Sayfayı atanmamışa al (çöp kutusu) */
  onUnassignPage?: (pageId: number) => void;
  /** Header'dan kontrol edilen odak cluster */
  focusClusterId?: number | null;
  onFocusCluster?: (id: number | null) => void;
  /** Header aramasından seçilen sayfa */
  focusPageId?: number | null;
  /** Neural map'in çektiği tüm sayfalar header aramasına aktarılır */
  onPagesLoaded?: (m: Map<number, { id: number; url: string; title?: string | null; isPillar: boolean }[]>) => void;
  /** Hiçbir cluster'a atanmamış sayfalar — neural map'te "Atanmamış" grubunda gösterilir */
  orphanPages?: { id: number; url: string; title?: string | null }[];
}

// ─── Language palette (neon) ──────────────────────────────────────────────────

const LANG_META: Record<string, { name: string; flag: string; accent: string; glow: string }> = {
  EN: { name: "English",   flag: "🇬🇧", accent: N.blue,   glow: "rgba(59,130,246,0.35)" },
  TR: { name: "Türkçe",   flag: "🇹🇷", accent: "#ec6461", glow: "rgba(236,100,97,0.35)" },
  AR: { name: "العربية",  flag: "🇸🇦", accent: N.green,  glow: "rgba(34,197,94,0.30)" },
  DE: { name: "Deutsch",  flag: "🇩🇪", accent: N.amber,  glow: "rgba(245,158,11,0.30)" },
  FR: { name: "Français", flag: "🇫🇷", accent: "#d42b29", glow: "rgba(212,43,41,0.30)" },
  RU: { name: "Русский",  flag: "🇷🇺", accent: N.red,    glow: "rgba(239,68,68,0.30)" },
  ES: { name: "Español",  flag: "🇪🇸", accent: "#f7b6b4", glow: "rgba(247,182,180,0.30)" },
  IT: { name: "Italiano", flag: "🇮🇹", accent: "#400b0a", glow: "rgba(22,25,80,0.28)" },
  ZH: { name: "中文",     flag: "🇨🇳", accent: "#771e1d", glow: "rgba(119,30,29,0.30)" },
  JA: { name: "日本語",   flag: "🇯🇵", accent: "#ea514f", glow: "rgba(234,81,79,0.30)" },
  KO: { name: "한국어",   flag: "🇰🇷", accent: N.cyan,   glow: "rgba(6,182,212,0.28)" },
};

const NEUTRAL_META = { name: "İçerik", flag: "🌐", accent: "#ec6461", glow: "rgba(236,100,97,0.28)" };

function getLangMeta(lang: string | null) {
  if (!lang) return NEUTRAL_META;
  return LANG_META[lang.toUpperCase()] ?? { name: lang, flag: "🌐", accent: N.cyan, glow: "rgba(6,182,212,0.28)" };
}

function getLangFromName(name: string): string | null {
  return name.match(/^\[([A-Z]{2})\] /)?.[1] ?? null;
}
function getDisplayName(name: string) {
  return name.replace(/^\[[A-Z]{2}\] /, "");
}
function countDescendants(cluster: BoardCluster): number {
  if (!cluster.children?.length) return 0;
  return cluster.children.reduce((s, c) => s + 1 + countDescendants(c), 0);
}

// ─── Flatten all clusters ─────────────────────────────────────────────────────

function flattenTree(tree: BoardCluster[]): BoardCluster[] {
  const out: BoardCluster[] = [];
  const walk = (nodes: BoardCluster[]) => {
    for (const n of nodes) {
      out.push(n);
      if (n.children) walk(n.children);
    }
  };
  walk(tree);
  return out;
}

// ─── Stats bar (dark neon) ────────────────────────────────────────────────────

function StatsBar({ tree, orphanCount, visibleLangCount }: {
  tree: BoardCluster[]; orphanCount: number; visibleLangCount: number;
}) {
  const allClusters = useMemo(() => flattenTree(tree), [tree]);
  const totalPages = allClusters.reduce((s, c) => s + (c.pageCount ?? 0), 0);

  const stats = [
    { icon: Globe2,     label: "Dil",          value: visibleLangCount,         color: N.cyan },
    { icon: StickyNote, label: "Toplam Sayfa",  value: totalPages + orphanCount, color: N.blue },
    { icon: Crown,      label: "Pillar",        value: tree.length,              color: N.amber },
    { icon: Layers,     label: "Cluster Grubu", value: allClusters.length,       color: N.purple },
    { icon: LayoutGrid, label: "Atanmamış",     value: orphanCount,              color: orphanCount > 0 ? N.red : N.green },
  ];

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {stats.map(({ icon: Icon, label, value, color }) => (
        <div
          key={label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 16px",
            borderRadius: 12,
            background: N.surface,
            border: `1px solid ${N.border}`,
            boxShadow: `0 0 12px ${color}15`,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `${color}15`,
              border: `1px solid ${color}30`,
            }}
          >
            <Icon size={15} style={{ color }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: N.textMuted, lineHeight: 1 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.2, marginTop: 2 }}>{value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Inline add cluster form ──────────────────────────────────────────────────

function AddClusterInline({ label, onAdd, parentId }: {
  label?: string;
  onAdd: (name: string, parentId?: number | null) => Promise<void>;
  parentId?: number | null;
}) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!val.trim()) return;
    setLoading(true);
    try { await onAdd(val.trim(), parentId ?? null); setVal(""); setOpen(false); }
    finally { setLoading(false); }
  };

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        fontSize: 12,
        color: N.textMuted,
        background: "transparent",
        border: `1px dashed ${N.border}`,
        borderRadius: 8,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = N.textSecondary; e.currentTarget.style.borderColor = N.purple; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = N.textMuted; e.currentTarget.style.borderColor = N.border; }}
    >
      <Plus size={12} />{label ?? "Yeni cluster ekle"}
    </button>
  );

  return (
    <div style={{ display: "flex", gap: 6 }}>
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setOpen(false); }}
        placeholder="Cluster adı..."
        style={{ height: 30, fontSize: 12, background: N.elevated, border: `1px solid ${N.border}`, color: N.textPrimary }}
        autoFocus
      />
      <button style={{ padding: "0 8px", background: `${N.purple}30`, border: `1px solid ${N.purple}50`, borderRadius: 6, color: N.purple, cursor: "pointer" }} onClick={submit} disabled={loading}>
        <Check size={13} />
      </button>
      <button style={{ padding: "0 8px", background: "transparent", border: `1px solid ${N.border}`, borderRadius: 6, color: N.textMuted, cursor: "pointer" }} onClick={() => setOpen(false)}>
        <X size={13} />
      </button>
    </div>
  );
}

// ─── Language picker dialog ───────────────────────────────────────────────────

function LangPickerDialog({ open, onClose, excludeLangs, title, onPick }: {
  open: boolean; onClose: () => void; excludeLangs?: Set<string>; title?: string; onPick: (lang: string) => void;
}) {
  const [search, setSearch] = useState("");
  const available = Object.entries(LANG_META).filter(([code]) =>
    !excludeLangs?.has(code) &&
    (search === "" || code.toLowerCase().includes(search.toLowerCase()) || LANG_META[code].name.toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setSearch(""); onClose(); } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Languages className="w-5 h-5" />{title ?? "Dil Seç"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Dil ara..." autoFocus className="h-9" />
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
            {available.map(([code, meta]) => (
              <button key={code} onClick={() => { onPick(code); setSearch(""); onClose(); }}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 hover:border-current transition-all text-left"
                style={{ borderColor: `${meta.accent}30` }}
              >
                <span className="text-xl leading-none">{meta.flag}</span>
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold truncate" style={{ color: meta.accent }}>{meta.name}</div>
                  <div className="text-[10px] text-muted-foreground">{code}</div>
                </div>
              </button>
            ))}
            {available.length === 0 && <div className="col-span-2 text-center py-6 text-muted-foreground text-sm">Dil bulunamadı</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Cluster drop zone ────────────────────────────────────────────────────────

function ClusterDropZone({ clusterId, children }: { clusterId: number; children: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `cluster-${clusterId}`, data: { kind: "cluster-zone", clusterId },
  });
  return (
    <div ref={setNodeRef} style={{ borderRadius: "0 0 10px 10px", transition: "background 0.15s", background: isOver ? `${N.purple}10` : "transparent", minHeight: 8 }}>
      {children}
    </div>
  );
}

function LangDropZone({ lang, children }: { lang: string | null; children: React.ReactNode }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `lang-${lang ?? "root"}`, data: { kind: "lang-zone", lang },
  });
  return (
    <div ref={setNodeRef} style={{ flex: 1, transition: "background 0.15s", background: isOver ? `${N.purple}08` : "transparent" }}>
      {children}
    </div>
  );
}

// ─── Draggable page row ───────────────────────────────────────────────────────

function DraggableBoardPage({ page, clusterId, accent, onSetPillar, onUnassignPage }: {
  page: BoardPage; clusterId: number; accent: string;
  onSetPillar?: (clusterId: number, pageId: number | null) => Promise<void>;
  onUnassignPage?: (pageId: number) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `page-${page.id}`,
    data: { kind: "page", pageId: page.id, sourceClusterId: clusterId, label: page.title || page.url },
  });
  const [pillarPending, setPillarPending] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleTogglePillar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onSetPillar) return;
    setPillarPending(true);
    try { await onSetPillar(clusterId, page.isPillar ? null : page.id); }
    finally { setPillarPending(false); }
  };

  return (
    <div
      ref={setNodeRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 8px",
        borderRadius: 6,
        transition: "background 0.12s",
        background: hovered ? `${N.elevated}` : "transparent",
        opacity: isDragging ? 0.3 : 1,
      }}
    >
      <button
        {...listeners} {...attributes}
        style={{ cursor: "grab", color: hovered ? N.textMuted : `${N.textMuted}40`, background: "none", border: "none", padding: 0, flexShrink: 0 }}
      >
        <GripVertical size={11} />
      </button>
      <div style={{ flexShrink: 0 }}>
        {page.isPillar
          ? <Crown size={11} style={{ color: N.amber }} />
          : page.isFeatured
          ? <Star size={11} style={{ color: N.amber, fill: N.amber }} />
          : <FileText size={11} style={{ color: N.textMuted }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <a href={page.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
          style={{ fontSize: 11, fontWeight: 500, color: N.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", textDecoration: "none" }}
        >
          {page.title || page.url}
        </a>
        <div style={{ fontSize: 9, color: N.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{page.url}</div>
      </div>
      {typeof page.score === "number" && (
        <span style={{ fontSize: 9, color: accent, background: `${accent}18`, padding: "1px 5px", borderRadius: 4, flexShrink: 0 }}>{page.score}</span>
      )}
      {onSetPillar && hovered && (
        <button
          onClick={handleTogglePillar}
          disabled={pillarPending}
          style={{
            padding: "3px 4px",
            borderRadius: 5,
            border: "none",
            background: page.isPillar ? `${N.amber}20` : "transparent",
            color: page.isPillar ? N.amber : N.textMuted,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Crown size={11} style={{ fill: page.isPillar ? N.amber : "transparent" }} />
        </button>
      )}
      {onUnassignPage && hovered && (
        <button
          title="Sayfayı atanmamışa al"
          onClick={(e) => { e.stopPropagation(); onUnassignPage(page.id); }}
          style={{ padding: "3px 4px", borderRadius: 5, border: "none", background: "transparent", color: N.red, cursor: "pointer", flexShrink: 0 }}
        >
          <Trash2 size={11} />
        </button>
      )}
    </div>
  );
}

// ─── Sub-cluster section ──────────────────────────────────────────────────────

function SubClusterSection({ cluster, parentId, lang, accent, onRename, onDelete, onAddCluster, onSetPillar, onUnassignPage, onSelectCluster, isSelected }: {
  cluster: BoardCluster; parentId: number | null; lang: string | null; accent: string;
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => void;
  onAddCluster: (name: string, parentId?: number | null) => Promise<void>;
  onSetPillar?: (clusterId: number, pageId: number | null) => Promise<void>;
  onUnassignPage?: (pageId: number) => void;
  onSelectCluster: (id: number) => void;
  isSelected: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(getDisplayName(cluster.name));
  const [hovered, setHovered] = useState(false);
  const { data: pagesData } = useGetClusterPages(cluster.id);
  const pages = (pagesData ?? []) as BoardPage[];
  const childClusters = cluster.children ?? [];
  const descendantCount = countDescendants(cluster);

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `board-cluster-${cluster.id}`,
    data: { kind: "cluster", clusterId: cluster.id, currentParentId: parentId, currentLang: lang, currentName: cluster.name, label: getDisplayName(cluster.name) },
  });

  const submitEdit = async () => {
    try { await onRename(cluster.id, draftName.trim()); setEditing(false); } catch { /* upstream */ }
  };

  return (
    <div
      ref={setDragRef}
      style={{
        marginLeft: 12,
        borderLeft: `2px solid ${isSelected ? accent : `${accent}30`}`,
        paddingLeft: 10,
        opacity: isDragging ? 0.4 : 1,
        transition: "opacity 0.15s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "4px 4px 4px 0",
          cursor: "pointer",
          background: isSelected ? `${accent}10` : "transparent",
          borderRadius: 6,
          transition: "background 0.15s",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onSelectCluster(cluster.id)}
      >
        <button
          {...listeners} {...attributes}
          style={{ cursor: "grab", color: hovered ? N.textMuted : `${N.textMuted}30`, background: "none", border: "none", padding: "0 2px", flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={11} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{ background: "none", border: "none", padding: 0, color: N.textMuted, cursor: "pointer", flexShrink: 0 }}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>

        {editing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4, flex: 1 }} onClick={(e) => e.stopPropagation()}>
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitEdit(); if (e.key === "Escape") setEditing(false); }}
              style={{ height: 24, fontSize: 11, background: N.elevated, border: `1px solid ${N.border}`, color: N.textPrimary }}
              autoFocus
            />
            <button style={{ padding: "2px 5px", background: `${N.green}20`, border: `1px solid ${N.green}40`, borderRadius: 4, color: N.green, cursor: "pointer" }} onClick={submitEdit}><Check size={11} /></button>
            <button style={{ padding: "2px 5px", background: "transparent", border: `1px solid ${N.border}`, borderRadius: 4, color: N.textMuted, cursor: "pointer" }} onClick={() => setEditing(false)}><X size={11} /></button>
          </div>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, flex: 1, minWidth: 0, wordBreak: "break-word", lineHeight: 1.3, color: isSelected ? accent : `${accent}cc` }}>
            {getDisplayName(cluster.name)}
          </span>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <span style={{ fontSize: 9, color: accent, background: `${accent}18`, padding: "1px 5px", borderRadius: 4, border: `1px solid ${accent}30` }}>
            {cluster.pageCount ?? pages.length} sayfa
          </span>
          {descendantCount > 0 && (
            <span style={{ fontSize: 9, color: N.textMuted, background: `${N.elevated}`, padding: "1px 5px", borderRadius: 4, border: `1px solid ${N.border}` }}>
              {descendantCount} alt
            </span>
          )}
        </div>

        {hovered && !editing && (
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
            <button style={{ padding: "2px 4px", background: "transparent", border: "none", borderRadius: 4, color: N.textMuted, cursor: "pointer" }} onClick={() => { setDraftName(getDisplayName(cluster.name)); setEditing(true); }}><Pencil size={10} /></button>
            <button style={{ padding: "2px 4px", background: "transparent", border: "none", borderRadius: 4, color: N.red, cursor: "pointer" }} onClick={() => onDelete(cluster.id)}><Trash2 size={10} /></button>
          </div>
        )}
      </div>

      {expanded && (
        <ClusterDropZone clusterId={cluster.id}>
          <div style={{ paddingBottom: 4, display: "flex", flexDirection: "column", gap: 1 }}>
            {pages.map((p) => <DraggableBoardPage key={p.id} page={p} clusterId={cluster.id} accent={accent} onSetPillar={onSetPillar} onUnassignPage={onUnassignPage} />)}
            {childClusters.map((child) => (
              <SubClusterSection
                key={child.id}
                cluster={child}
                parentId={cluster.id}
                lang={lang}
                accent={accent}
                onRename={onRename}
                onDelete={onDelete}
                onUnassignPage={onUnassignPage}
                onAddCluster={onAddCluster}
                onSetPillar={onSetPillar}
                onSelectCluster={onSelectCluster}
                isSelected={false}
              />
            ))}
            <AddClusterInline label="Alt cluster ekle" onAdd={onAddCluster} parentId={cluster.id} />
          </div>
        </ClusterDropZone>
      )}
    </div>
  );
}

// ─── Pillar card ──────────────────────────────────────────────────────────────

function PillarCard({ cluster, index, lang, onRename, onDelete, onAddCluster, onSetPillar, onUnassignPage, selected, onSelectToggle, onSelectCluster, isHighlighted }: {
  cluster: BoardCluster; index: number; lang: string | null;
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => void;
  onAddCluster: (name: string, parentId?: number | null) => Promise<void>;
  onSetPillar?: (clusterId: number, pageId: number | null) => Promise<void>;
  onUnassignPage?: (pageId: number) => void;
  selected: boolean;
  onSelectToggle: (id: number, checked: boolean) => void;
  onSelectCluster: (id: number) => void;
  isHighlighted: boolean;
}) {
  const meta = getLangMeta(lang);
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(getDisplayName(cluster.name));
  const [hovered, setHovered] = useState(false);
  const { data: pagesData } = useGetClusterPages(cluster.id);
  const pages = (pagesData ?? []) as BoardPage[];
  const childClusters = cluster.children ?? [];
  const directPages = pages.filter((p) => !childClusters.some((c) => c.id === (p as any).clusterId));
  const descendantCount = countDescendants(cluster);

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `board-cluster-${cluster.id}`,
    data: { kind: "cluster", clusterId: cluster.id, currentParentId: null, currentLang: lang, currentName: cluster.name, label: getDisplayName(cluster.name) },
  });

  const submitEdit = async () => {
    try { await onRename(cluster.id, draftName.trim()); setEditing(false); } catch { /* upstream */ }
  };

  return (
    <div
      ref={setDragRef}
      style={{
        borderRadius: 12,
        border: `2px solid ${isHighlighted ? meta.accent : selected ? `${meta.accent}80` : `${meta.accent}35`}`,
        overflow: "hidden",
        boxShadow: isHighlighted
          ? `0 0 24px ${meta.glow}, 0 0 48px ${meta.glow}`
          : selected
          ? `0 0 14px ${meta.glow}`
          : `0 0 8px ${meta.glow}`,
        opacity: isDragging ? 0.4 : 1,
        transition: "all 0.2s",
        background: N.surface,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          cursor: "pointer",
          background: `linear-gradient(135deg, ${meta.accent}18, ${meta.accent}06)`,
          borderBottom: `1px solid ${meta.accent}25`,
        }}
        onClick={() => { if (!editing) { setExpanded(!expanded); onSelectCluster(cluster.id); } }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <button
          {...listeners} {...attributes}
          style={{ cursor: "grab", color: hovered ? N.textMuted : `${N.textMuted}30`, background: "none", border: "none", padding: 0, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} />
        </button>

        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            color: "#fff",
            background: meta.accent,
            flexShrink: 0,
          }}
        >
          {index}
        </div>

        {editing ? (
          <div style={{ display: "flex", flex: 1, gap: 5 }} onClick={(e) => e.stopPropagation()}>
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitEdit(); if (e.key === "Escape") setEditing(false); }}
              style={{ height: 28, fontSize: 12, background: N.elevated, border: `1px solid ${N.border}`, color: N.textPrimary }}
              autoFocus
            />
            <button style={{ padding: "0 8px", background: `${N.green}25`, border: `1px solid ${N.green}50`, borderRadius: 6, color: N.green, cursor: "pointer" }} onClick={submitEdit}><Check size={12} /></button>
            <button style={{ padding: "0 8px", background: "transparent", border: `1px solid ${N.border}`, borderRadius: 6, color: N.textMuted, cursor: "pointer" }} onClick={() => setEditing(false)}><X size={12} /></button>
          </div>
        ) : (
          <span style={{ fontSize: 13, fontWeight: 700, flex: 1, minWidth: 0, wordBreak: "break-word", lineHeight: 1.3, color: meta.accent }}>
            {getDisplayName(cluster.name)}
          </span>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
          <span style={{ fontSize: 9, color: meta.accent, background: `${meta.accent}18`, padding: "2px 6px", borderRadius: 4, border: `1px solid ${meta.accent}35` }}>
            {cluster.pageCount ?? pages.length} sayfa
          </span>
          {descendantCount > 0 && (
            <span style={{ fontSize: 9, color: N.textMuted, background: N.elevated, padding: "2px 6px", borderRadius: 4, border: `1px solid ${N.border}` }}>
              {descendantCount} alt
            </span>
          )}
          {hovered && !editing && (
            <div style={{ display: "flex", gap: 2 }} onClick={(e) => e.stopPropagation()}>
              <button style={{ padding: "2px 4px", background: selected ? meta.accent : `${meta.accent}20`, border: `1px solid ${meta.accent}40`, borderRadius: 4, color: selected ? "#fff" : meta.accent, cursor: "pointer", fontSize: 9 }} onClick={() => onSelectToggle(cluster.id, !selected)} title="Birleştirmek için seç"><Check size={10} /></button>
              <button style={{ padding: "2px 4px", background: "transparent", border: "none", borderRadius: 4, color: N.textMuted, cursor: "pointer" }} onClick={() => { setDraftName(getDisplayName(cluster.name)); setEditing(true); }}><Pencil size={11} /></button>
              <button style={{ padding: "2px 4px", background: "transparent", border: "none", borderRadius: 4, color: N.red, cursor: "pointer" }} onClick={() => onDelete(cluster.id)}><Trash2 size={11} /></button>
            </div>
          )}
          {!editing && (
            <span style={{ color: meta.accent }}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
          )}
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <ClusterDropZone clusterId={cluster.id}>
          <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 2, background: `${N.surface}`, minHeight: 32 }}>
            {childClusters.map((child) => (
              <SubClusterSection
                key={child.id}
                cluster={child}
                parentId={cluster.id}
                lang={lang}
                accent={meta.accent}
                onRename={onRename}
                onDelete={onDelete}
                onAddCluster={onAddCluster}
                onSetPillar={onSetPillar}
                onUnassignPage={onUnassignPage}
                onSelectCluster={onSelectCluster}
                isSelected={false}
              />
            ))}
            {directPages.map((p) => <DraggableBoardPage key={p.id} page={p} clusterId={cluster.id} accent={meta.accent} onSetPillar={onSetPillar} onUnassignPage={onUnassignPage} />)}
            <div style={{ paddingTop: 6 }}>
              <AddClusterInline label="Alt cluster ekle" onAdd={onAddCluster} parentId={cluster.id} />
            </div>
          </div>
        </ClusterDropZone>
      )}
    </div>
  );
}

// ─── Language column ──────────────────────────────────────────────────────────

function LangColumn({ lang, pillars, onRename, onDelete, onAddCluster, onSetPillar, onUnassignPage, selectedIds, onSelectToggle, onDeleteLang, onChangeLang, excludeLangsFromPicker, selectedClusterId, onSelectCluster }: {
  lang: string | null; pillars: BoardCluster[];
  onRename: (id: number, name: string) => Promise<void>;
  onDelete: (id: number) => void;
  onAddCluster: (name: string, parentId?: number | null) => Promise<void>;
  onSetPillar?: (clusterId: number, pageId: number | null) => Promise<void>;
  onUnassignPage?: (pageId: number) => void;
  selectedIds: Set<number>;
  onSelectToggle: (id: number, checked: boolean) => void;
  onDeleteLang?: (lang: string | null, clusterIds: number[]) => void;
  onChangeLang?: (oldLang: string | null, newLang: string, pillars: BoardCluster[]) => Promise<void>;
  excludeLangsFromPicker: Set<string>;
  selectedClusterId: number | null;
  onSelectCluster: (id: number) => void;
}) {
  const meta = getLangMeta(lang);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [changeLangOpen, setChangeLangOpen] = useState(false);
  const [changeLangPending, setChangeLangPending] = useState(false);

  const allInColumn = useMemo(() => flattenTree(pillars), [pillars]);
  const totalPages = allInColumn.reduce((s, c) => s + (c.pageCount ?? 0), 0);

  const handleAddWithLang = async (name: string, parentId?: number | null) => {
    const prefixed = (lang && !parentId) ? `[${lang}] ${name}` : name;
    await onAddCluster(prefixed, parentId);
  };

  const confirmDeleteAll = () => {
    const allIds: number[] = [];
    const walk = (nodes: BoardCluster[]) => { for (const n of nodes) { allIds.push(n.id); if (n.children) walk(n.children); } };
    walk(pillars);
    onDeleteLang?.(lang, allIds);
    setConfirmDelete(false);
  };

  const handleChangeLang = async (newLang: string) => {
    if (!onChangeLang) return;
    setChangeLangPending(true);
    try { await onChangeLang(lang, newLang, pillars); }
    finally { setChangeLangPending(false); }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          borderRadius: 16,
          border: `2px solid ${meta.accent}35`,
          overflow: "hidden",
          boxShadow: `0 0 20px ${meta.glow}`,
          background: N.surface,
        }}
      >
        {/* Column header */}
        <div
          style={{
            padding: "14px 16px",
            background: `linear-gradient(135deg, ${meta.accent}18, ${meta.accent}06)`,
            borderBottom: `1px solid ${meta.accent}25`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 22 }}>{meta.flag}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: meta.accent, margin: 0 }}>{meta.name}</h3>
              <p style={{ fontSize: 10, color: N.textMuted, margin: 0 }}>
                {totalPages} sayfa · {allInColumn.length} cluster
              </p>
            </div>
            <button
              onClick={() => setChangeLangOpen(true)}
              disabled={changeLangPending}
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 20,
                border: `1px solid ${meta.accent}40`,
                background: `${meta.accent}15`,
                color: meta.accent,
                cursor: "pointer",
              }}
            >
              {changeLangPending ? "..." : (lang ?? "ROOT")} ✎
            </button>
            {onDeleteLang && (
              <button
                onClick={() => pillars.length === 0 ? onDeleteLang(lang, []) : setConfirmDelete(true)}
                style={{ padding: 6, borderRadius: 6, border: "none", background: "transparent", color: N.textMuted, cursor: "pointer" }}
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 16, fontSize: 10, color: `${meta.accent}bb` }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Crown size={10} style={{ color: N.amber }} />{pillars.length} Pillar</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Layers size={10} />{allInColumn.length} Cluster</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><FileText size={10} />{totalPages} Sayfa</span>
          </div>
        </div>

        <div style={{ height: 1, background: `linear-gradient(90deg, ${meta.accent}50, transparent)` }} />

        <LangDropZone lang={lang}>
          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
            {pillars.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: N.textMuted, fontSize: 13 }}>
                <FolderTree size={32} style={{ display: "block", margin: "0 auto 8px", opacity: 0.2 }} />
                Henüz cluster yok
              </div>
            ) : (
              pillars.map((pillar, i) => (
                <PillarCard
                  key={pillar.id}
                  cluster={pillar}
                  index={i + 1}
                  lang={lang}
                  onRename={onRename}
                  onDelete={onDelete}
                  onAddCluster={onAddCluster}
                  onSetPillar={onSetPillar}
                  onUnassignPage={onUnassignPage}
                  selected={selectedIds.has(pillar.id)}
                  onSelectToggle={onSelectToggle}
                  onSelectCluster={onSelectCluster}
                  isHighlighted={selectedClusterId === pillar.id}
                />
              ))
            )}
          </div>
        </LangDropZone>

        <div style={{ padding: "10px 12px", borderTop: `1px solid ${N.border}`, background: N.bg }}>
          <AddClusterInline label={`${meta.name} pillar ekle`} onAdd={handleAddWithLang} parentId={null} />
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{meta.flag} {meta.name} dilini sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu dil sütunundaki <strong>{pillars.length} pillar</strong> ve toplam <strong>{allInColumn.length} cluster</strong> silinecek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAll} className="bg-red-500 hover:bg-red-600">Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <LangPickerDialog
        open={changeLangOpen}
        onClose={() => setChangeLangOpen(false)}
        excludeLangs={excludeLangsFromPicker}
        title={`"${meta.name}" dilini değiştir`}
        onPick={handleChangeLang}
      />
    </>
  );
}

// ─── View toggle button ───────────────────────────────────────────────────────

function ViewToggle({ view, onChange }: { view: "neural" | "tree"; onChange: (v: "neural" | "tree") => void }) {
  return (
    <div
      style={{
        display: "flex",
        background: N.surface,
        border: `1px solid ${N.border}`,
        borderRadius: 10,
        padding: 3,
        gap: 2,
      }}
    >
      {(["neural", "tree"] as const).map((v) => {
        const active = view === v;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              borderRadius: 7,
              border: "none",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.15s",
              background: active ? N.purple : "transparent",
              color: active ? "#fff" : N.textMuted,
              boxShadow: active ? `0 0 14px ${N.purple}60` : "none",
            }}
          >
            {v === "neural" ? <Share2 size={13} /> : <List size={13} />}
            {v === "neural" ? "Nöral Harita" : "Ağaç Görünümü"}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main board ───────────────────────────────────────────────────────────────

export function ClusterBoard({
  tree,
  domainId: _domainId,
  orphanCount,
  onRename,
  onDelete,
  onAddCluster,
  onSetPillar,
  selectedIds,
  onSelectToggle,
  onMerge,
  view: externalView = "neural",
  domainUrl = "",
  onMovePage,
  onMoveCluster,
  onUnassignPage,
  focusClusterId,
  onFocusCluster,
  focusPageId,
  onPagesLoaded,
  orphanPages = [],
}: ClusterBoardProps) {
  const view = externalView;
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);
  const [hiddenLangs, setHiddenLangs] = useState<Set<string | null>>(new Set());

  // ── Neural harita için tüm cluster sayfalarını lazy fetch et ──────────────
  type MapPage = { id: number; url: string; title?: string | null; isPillar: boolean; score?: number | null };
  const [pagesMap, setPagesMap] = useState<Map<number, MapPage[]>>(new Map());
  useEffect(() => {
    if (view !== "neural") return;
    let cancelled = false;
    const flatClusters: BoardCluster[] = [];
    const walk = (arr: BoardCluster[]) => { for (const c of arr) { flatClusters.push(c); if (c.children) walk(c.children); } };
    walk(tree);
    (async () => {
      const map = new Map<number, MapPage[]>();
      await Promise.all(flatClusters.map(async (c) => {
        try {
          const pages = await getClusterPages(c.id);
          if (!cancelled) map.set(c.id, pages);
        } catch { /* ignore */ }
      }));
      if (!cancelled) { setPagesMap(new Map(map)); onPagesLoaded?.(new Map(map)); }
    })();
    return () => { cancelled = true; };
  }, [view, tree, onPagesLoaded]);

  const allClusters = useMemo(() => flattenTree(tree), [tree]);

  const selectedCluster = useMemo(
    () => (selectedClusterId != null ? allClusters.find((c) => c.id === selectedClusterId) ?? null : null),
    [selectedClusterId, allClusters]
  );

  const langColumns = useMemo(() => {
    const groups = new Map<string | null, BoardCluster[]>();
    for (const c of tree) {
      const lang = getLangFromName(c.name);
      const validLang = lang && Object.prototype.hasOwnProperty.call(LANG_META, lang.toUpperCase()) ? lang : null;
      if (!groups.has(validLang)) groups.set(validLang, []);
      groups.get(validLang)!.push(c);
    }
    for (const lang of hiddenLangs) groups.delete(lang);
    const hasLangColumn = [...groups.keys()].some((k) => k !== null);
    if (hasLangColumn) groups.delete(null);
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === null) return -1; if (b === null) return 1; return a.localeCompare(b);
    });
  }, [tree, hiddenLangs]);

  const existingLangs = useMemo(() => {
    const s = new Set<string>();
    for (const [lang] of langColumns) if (lang) s.add(lang);
    return s;
  }, [langColumns]);

  const handleDeleteLang = (lang: string | null, clusterIds: number[]) => {
    for (const id of clusterIds) onDelete(id);
    setHiddenLangs((prev) => new Set([...prev, lang]));
  };

  const handleChangeLang = async (oldLang: string | null, newLang: string, pillars: BoardCluster[]) => {
    for (const pillar of pillars) {
      await onRename(pillar.id, `[${newLang}] ${getDisplayName(pillar.name)}`);
    }
    if (!oldLang) setHiddenLangs((prev) => new Set([...prev, null]));
    setHiddenLangs((prev) => { const next = new Set(prev); next.delete(newLang); return next; });
  };

  return (
    <div style={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>

        {/* Neural map OR tree view */}
        <div style={{ flex: 1, minWidth: 0, position: "relative", overflow: "hidden", height: "100%" }}>
          {view === "neural" ? (
            <div style={{ width: "100%", height: "100%" }}>
              <ClusterNeuralMap
                tree={tree}
                domainUrl={domainUrl}
                pagesMap={pagesMap}
                selectedClusterId={selectedClusterId}
                onSelectCluster={(id) => setSelectedClusterId(id)}
                onAddCluster={onAddCluster}
                onMovePage={onMovePage}
                onMoveCluster={onMoveCluster}
                onSetPillar={onSetPillar}
                focusClusterId={focusClusterId}
                onFocusCluster={onFocusCluster}
                focusPageId={focusPageId}
                persistKey={String(_domainId)}
                onDeleteCluster={onDelete}
                orphanPages={orphanPages}
              />
            </div>
          ) : (
            /* Tree (sütun) view — kolonlar içeriğe göre aşağı genişler,
               tüm sayfa scroll olur; responsive genişlik (sığmazsa yatay scroll) */
            <div style={{
              position: "absolute", inset: 0,
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
              overflowX: "auto",
              overflowY: "auto",
              padding: "20px 24px 48px",
            }}>
                {langColumns.map(([lang, pillars]) => (
                  <div key={lang ?? "root"} style={{ flex: "1 1 0", minWidth: 340, maxWidth: 520 }}>
                    <LangColumn
                      lang={lang}
                      pillars={pillars}
                      onRename={onRename}
                      onDelete={onDelete}
                      onAddCluster={onAddCluster}
                      onSetPillar={onSetPillar}
                      onUnassignPage={onUnassignPage}
                      selectedIds={selectedIds}
                      onSelectToggle={onSelectToggle}
                      onDeleteLang={handleDeleteLang}
                      onChangeLang={handleChangeLang}
                      excludeLangsFromPicker={existingLangs}
                      selectedClusterId={selectedClusterId}
                      onSelectCluster={(id) => setSelectedClusterId(prev => prev === id ? null : id)}
                    />
                  </div>
                ))}

                {/* Atanmamış sayfalar — sürükleyip bir cluster'a bırakılabilir */}
                {orphanPages.length > 0 && (
                  <div style={{ flex: "1 1 0", minWidth: 300, maxWidth: 460 }}>
                    <div style={{ display: "flex", flexDirection: "column", borderRadius: 16, border: "2px solid #70706c40", overflow: "hidden", boxShadow: "0 0 20px rgba(0,0,0,0.35)", background: N.surface }}>
                      <div style={{ padding: "14px 16px", background: "linear-gradient(135deg, #70706c20, #70706c08)", borderBottom: "1px solid #70706c25" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 22 }}>📂</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 700, color: "#70706c", margin: 0 }}>Atanmamış</h3>
                            <p style={{ fontSize: 10, color: N.textMuted, margin: 0 }}>{orphanPages.length} sayfa · sürükleyip cluster'a ata</p>
                          </div>
                        </div>
                      </div>
                      <div style={{ height: 1, background: "linear-gradient(90deg, #70706c50, transparent)" }} />
                      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                        {orphanPages.map((p) => (
                          <DraggableBoardPage key={p.id} page={p as BoardPage} clusterId={-1} accent="#70706c" />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {langColumns.length === 0 && (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "96px 0", color: N.textMuted, width: "100%" }}>
                    <FolderTree size={64} style={{ marginBottom: 16, opacity: 0.15 }} />
                    <p style={{ fontSize: 16, fontWeight: 500, color: N.textSecondary, marginBottom: 4 }}>Henüz cluster oluşturulmadı</p>
                    <p style={{ fontSize: 13, color: N.textMuted }}>Taramayı tamamlayın; cluster'lar sayfa diline göre otomatik oluşturulur</p>
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedCluster && (
          <ClusterDetailPanel
            cluster={selectedCluster}
            allClusters={allClusters}
            onClose={() => setSelectedClusterId(null)}
          />
        )}
      </div>
  );
}
